<template>
  <PmTooltipPure :class="[componentClass.root, classes]" :style="styles">
    <PmIconPure
      v-if="icon"
      :class="componentClass.element('icon')"
      :name="icon"
      size="small"
      @click="emit('click')"
      @mouseenter="emit('mouseenter')"
      @mouseleave="emit('mouseleave')"
    />

    <template v-if="hasTooltip" #tooltip>
      <slot name="tooltip">
        <template v-if="prefix">
          <strong>{{ prefix }}:</strong>
        </template>

        {{ labelNormalized }}
      </slot>
    </template>
  </PmTooltipPure>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useComponentClass } from '@thomasaull-shared/composables'
import { STATUS_TRAVEL_AND_HOTEL_LOOKUP } from '@/constants/persoplan'
import {
  stringifyValueWithStatus,
  extractStatusAndValueFromString,
} from '@/utilities/string'
import { lookup } from '@/utilities/misc'
import PmTooltipPure from '../PmTooltipPure.vue'
import PmIconPure, {
  type Props as PropsIconPure,
} from '../PmIcon/PmIconPure.vue'
import { useTravelAndHotelStatus } from '@/composition/useTravelAndHotelStatus'
import { useHasSlotContent } from '@/composition/useHasSlotContent'

export type Props = {
  type:
    | 'hotel'
    | 'travel'
    | 'note'
    | 'resourceRequest'
    | 'driver'
    | 'vehicle'
    | 'timesLocked'
    | 'collectsAllJobsOfProject'
  label?: string
  icon?: PropsIconPure['name']
  size?: 'small' | 'default'
}

const props = withDefaults(defineProps<Props>(), {
  size: 'small',
})

export type Emits = {
  click: []
  mouseenter: []
  mouseleave: []
}

const emit = defineEmits<Emits>()

const componentClass = useComponentClass({ props })
const hasSlotContent = useHasSlotContent(['tooltip'])

const labelNormalized = computed(() => {
  if (props.type === 'timesLocked') return 'Zeiten sind als fertig markiert'
  if (props.type === 'collectsAllJobsOfProject')
    return 'Alle Jobs des Projektes werden zusammengefasst'

  // For travel/hotel
  return stringifyValueWithStatus(props.label)
})

const status = computed(() => {
  const statusId = extractStatusAndValueFromString(props.label)?.statusId
  if (!statusId) return

  return lookup(statusId, STATUS_TRAVEL_AND_HOTEL_LOOKUP)
})

const travelAndHotelStatus = useTravelAndHotelStatus({
  status: status,
})

const classes = computed(() => {
  return [
    travelAndHotelStatus.classes.value,
    componentClass.propModifier({ name: 'size' }),
  ]
})

const styles = computed(() => {
  return {
    color: travelAndHotelStatus.color.value,
  }
})

const prefix = computed(() => {
  if (props.type === 'hotel') return 'Hotel'
  if (props.type === 'travel') return 'Travel'
  if (props.type === 'note') return 'Bemerkung'

  return undefined
})

const lookupIcon: Partial<Record<Props['type'], PropsIconPure['name']>> = {
  hotel: 'bed',
  travel: 'direction',
  note: 'note',
  driver: 'driver',
  vehicle: 'vehicle',
  timesLocked: 'lock',
  collectsAllJobsOfProject: 'collection',
}

const icon = computed<PropsIconPure['name'] | undefined>(() => {
  if (props.icon) return props.icon
  return lookupIcon[props.type]
})

const hasTooltip = computed(() => {
  if (labelNormalized.value) return true
  if (hasSlotContent.value.tooltip) return true

  return false
})
</script>

<style lang="scss" scoped>
.PmInfoIconPure {
  $block: &;

  &--sizeSmall {
    @include cssVar.define($block, 'size', 16px);
  }

  &--sizeDefault {
    @include cssVar.define($block, 'size', 20px);
  }

  position: relative;
  flex: none;

  &-icon {
    border-radius: constant.$borderRadius-default;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: cssVar.use($block, 'size');
    height: cssVar.use($block, 'size');

    &:hover {
      box-shadow: 0 0 0 2px color.$key;
    }
  }
}
</style>
